/**
 * One WebSocket per browser tab. Survives React Strict Mode and duplicate effect runs.
 */

import { ClientWebSocket } from "./client"
import type { WebsocketCredentials, WsConnectionState } from "./types"

let sharedClient: ClientWebSocket | null = null
let sharedToken: string | null = null
let providerMounts = 0
let pendingRelease: number | null = null
let connectPromise: Promise<ClientWebSocket> | null = null

function isLive(client: ClientWebSocket): boolean {
  const s = client.connectionState
  return s === "connected" || s === "connecting"
}

export function acquireSharedWebSocket(
  creds: WebsocketCredentials,
  onStateChange: (state: WsConnectionState) => void
): ClientWebSocket {
  if (pendingRelease !== null) {
    clearTimeout(pendingRelease)
    pendingRelease = null
  }

  if (
    sharedClient &&
    sharedToken === creds.token &&
    isLive(sharedClient)
  ) {
    sharedClient.setOnStateChange(onStateChange)
    return sharedClient
  }

  if (sharedClient) {
    sharedClient.disconnect()
    sharedClient = null
    sharedToken = null
  }

  sharedToken = creds.token
  sharedClient = new ClientWebSocket({ onStateChange })
  sharedClient.connect(creds)
  return sharedClient
}

/** Serialize bootstrap so concurrent Strict Mode effects share one connect. */
export function acquireSharedWebSocketAsync(
  creds: WebsocketCredentials,
  onStateChange: (state: WsConnectionState) => void
): Promise<ClientWebSocket> {
  if (
    sharedClient &&
    sharedToken === creds.token &&
    isLive(sharedClient)
  ) {
    sharedClient.setOnStateChange(onStateChange)
    return Promise.resolve(sharedClient)
  }

  if (connectPromise && sharedToken === creds.token) {
    return connectPromise.then((client) => {
      client.setOnStateChange(onStateChange)
      return client
    })
  }

  connectPromise = Promise.resolve().then(() => {
    const client = acquireSharedWebSocket(creds, onStateChange)
    connectPromise = null
    return client
  })
  return connectPromise
}

export function registerWebsocketProviderMount(): void {
  if (pendingRelease !== null) {
    clearTimeout(pendingRelease)
    pendingRelease = null
  }
  providerMounts += 1
}

export function unregisterWebsocketProviderMount(): void {
  providerMounts = Math.max(0, providerMounts - 1)
  if (providerMounts > 0) return

  pendingRelease = window.setTimeout(() => {
    pendingRelease = null
    if (providerMounts > 0) return
    connectPromise = null
    sharedClient?.disconnect()
    sharedClient = null
    sharedToken = null
  }, 0)
}

export function forceReleaseSharedWebSocket(): void {
  if (pendingRelease !== null) {
    clearTimeout(pendingRelease)
    pendingRelease = null
  }
  providerMounts = 0
  connectPromise = null
  sharedClient?.disconnect()
  sharedClient = null
  sharedToken = null
}

export function getSharedWebSocket(): ClientWebSocket | null {
  return sharedClient
}
